/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    TextField,
    MenuItem,
} from "@mui/material";
import { AutoGraphOutlined, AddchartOutlined } from "@mui/icons-material";

import {
    selectActiveBusinessId,
    selectActiveBusinessRole,
} from "../../redux/selectors/collectSelector";
import {
    listPredictions,
    getPrediction,
    generatePrediction,
    applyPrediction,
} from "../../redux/services/predictionService";
import { fetchBusinessActivity, IActivityItem } from "../../redux/services/collectTaskService";
import { submitListCustomers, submitListInvoices } from "../../redux/services/collectService";
import {
    IPredictionAnalysis,
    ICollectCustomer,
    ICollectInvoice,
    PredictionRiskLevel,
    PREDICTION_RISK_LEVEL_LABELS,
} from "../../utility/types/collectTypes";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    FlexBetween,
    CollectInfoBanner,
} from "./styles";
import PredictionSummary from "./predictions/PredictionSummary";
import PredictionCharts from "./predictions/PredictionCharts";
import WhatIfSimulator from "./predictions/WhatIfSimulator";
import SavedScenarios from "./predictions/SavedScenarios";

// ─────────────────────────────────────────────────────────────────────────
// CollectPredictions (Stage 2J) — Customer Prediction Analysis.
//
// Forecasts how likely a customer (and optionally a specific invoice) is to
// pay on time, late, or not at all, plus a projected-receivables impact.
// Read-only for staff; owners/admins can generate, simulate what-if scenarios,
// and apply the recommended recovery action. Every prediction is an ESTIMATE
// (disclaimer always shown) and never messages the customer.
// ─────────────────────────────────────────────────────────────────────────

const FONT = "'Montserrat', sans-serif";

const RISK_COLORS: Record<PredictionRiskLevel, string> = {
    low: "#16a34a",
    medium: "#f59e0b",
    high: "#ea580c",
    critical: "#dc2626",
};

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
    prediction_generated: "Prediction generated",
    prediction_scenario_simulated: "Scenario simulated",
    prediction_applied: "Prediction applied",
};

const CUSTOMER_LEVEL_VALUE = "__customer_level__";

function fmtDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

const CollectPredictions: React.FC = () => {
    const toast = useToast();
    const businessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const canMutate = role === "owner" || role === "admin";

    const [predictions, setPredictions] = useState<IPredictionAnalysis[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<IPredictionAnalysis | null>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [applying, setApplying] = useState(false);

    // Generate dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [customerOptions, setCustomerOptions] = useState<ICollectCustomer[]>([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] =
        useState<ICollectCustomer | null>(null);
    const [invoiceOptions, setInvoiceOptions] = useState<ICollectInvoice[]>([]);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>("");
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [activities, setActivities] = useState<IActivityItem[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(false);

    const load = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await listPredictions(businessId, { limit: 50 });
            setPredictions(res.items || []);
            setTotal(res.total || 0);
            // Auto-select the most recent prediction on first load.
            setSelected((prev) => {
                if (prev) return prev;
                return res.items && res.items.length > 0 ? res.items[0] : null;
            });
        } catch (err: any) {
            setError(err.message || "Could not load predictions");
        } finally {
            setLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSelect = async (prediction: IPredictionAnalysis) => {
        if (!businessId) return;
        if (selected?._id === prediction._id) return;
        setLoadingDetail(true);
        try {
            // Re-fetch the full document (list rows already carry it, but this
            // guarantees fresh scenarios/charts).
            const full = await getPrediction(businessId, prediction._id);
            setSelected(full);
        } catch (err: any) {
            toast.error(err.message || "Could not load prediction");
        } finally {
            setLoadingDetail(false);
        }
    };

    // ── Generate dialog: customer search ────────────────────────────────
    const searchCustomers = useCallback(
        async (q: string) => {
            if (!businessId) return;
            setCustomerLoading(true);
            try {
                const res = await submitListCustomers(businessId, {
                    q: q || undefined,
                    limit: 20,
                });
                setCustomerOptions(res.items || []);
            } catch {
                setCustomerOptions([]);
            } finally {
                setCustomerLoading(false);
            }
        },
        [businessId],
    );

    const openDialog = () => {
        setSelectedCustomer(null);
        setSelectedInvoiceId("");
        setInvoiceOptions([]);
        setDialogOpen(true);
        searchCustomers("");
    };

    // When a customer is picked, load their invoices for the optional picker.
    useEffect(() => {
        let active = true;
        const run = async () => {
            if (!businessId || !selectedCustomer) {
                setInvoiceOptions([]);
                setSelectedInvoiceId("");
                setInvoiceLoading(false);
                return;
            }
            setInvoiceLoading(true);
            try {
                const res = await submitListInvoices(businessId, {
                    customerId: selectedCustomer._id,
                    limit: 50,
                });
                if (active) {
                    const invoices = res.items || [];
                    setInvoiceOptions(invoices);
                    setSelectedInvoiceId(
                        (current) =>
                            current || invoices[0]?._id || CUSTOMER_LEVEL_VALUE,
                    );
                }
            } catch {
                if (active) {
                    setInvoiceOptions([]);
                    setSelectedInvoiceId(CUSTOMER_LEVEL_VALUE);
                }
            } finally {
                if (active) setInvoiceLoading(false);
            }
        };
        run();
        return () => {
            active = false;
        };
    }, [businessId, selectedCustomer]);

    const primaryInvoiceRequired = invoiceOptions.length > 0;

    useEffect(() => {
        let active = true;
        const run = async () => {
            if (!businessId || !selected) {
                setActivities([]);
                return;
            }
            setActivitiesLoading(true);
            try {
                const query = selected.invoiceId
                    ? {
                        invoiceId: selected.invoiceId,
                        limit: 8,
                        types: [
                            "prediction_generated",
                            "prediction_scenario_simulated",
                            "prediction_applied",
                        ],
                    }
                    : {
                        customerId: selected.customerId,
                        limit: 8,
                        types: [
                            "prediction_generated",
                            "prediction_scenario_simulated",
                            "prediction_applied",
                        ],
                    };
                const res = await fetchBusinessActivity(businessId, query);
                if (active) setActivities(res.items || []);
            } catch {
                if (active) setActivities([]);
            } finally {
                if (active) setActivitiesLoading(false);
            }
        };
        run();
        return () => {
            active = false;
        };
    }, [businessId, selected]);

    const handleGenerate = async () => {
        if (!businessId || !selectedCustomer) return;
        if (!selectedInvoiceId) {
            toast.error("Select an invoice to generate an invoice-based prediction.");
            return;
        }
        setGenerating(true);
        try {
            const created = await generatePrediction(businessId, {
                customerId: selectedCustomer._id,
                invoiceId:
                    selectedInvoiceId === CUSTOMER_LEVEL_VALUE
                        ? undefined
                        : selectedInvoiceId,
            });
            toast.success("Prediction generated.");
            setDialogOpen(false);
            setSelected(created);
            setPredictions((prev) => [created, ...prev]);
            setTotal((t) => t + 1);
        } catch (err: any) {
            toast.error(err.message || "Could not generate prediction");
        } finally {
            setGenerating(false);
        }
    };

    const handleApply = async () => {
        if (!businessId || !selected) return;
        setApplying(true);
        try {
            const res = await applyPrediction(businessId, selected._id, true);
            toast.success(
                res.recommendationApplied
                    ? "Applied — a task was created for your team."
                    : "Recommended action recorded.",
            );
            setSelected(res.prediction);
        } catch (err: any) {
            toast.error(err.message || "Could not apply prediction");
        } finally {
            setApplying(false);
        }
    };

    const handleScenarioSaved = (updated: IPredictionAnalysis) => {
        setSelected(updated);
        setPredictions((prev) =>
            prev.map((p) => (p._id === updated._id ? updated : p)),
        );
    };

    const charts = useMemo(() => selected?.chartData || [], [selected]);

    return (
        <CollectPageWrapper>
            <CollectContentArea>
                {/* History + generate */}
                <CollectSectionCard>
                    <CollectSectionHeader>
                        <FlexBetween>
                            <Box>
                                <CollectSectionTitle>
                                    Invoice receivables predictions
                                </CollectSectionTitle>
                                <CollectSectionSubtitle>
                                    Forecast how likely a specific invoice or open
                                    receivable is to be paid on time, late, or not at
                                    all. Invoice-based predictions are the primary
                                    flow; customer-level forecasts are only used as a
                                    fallback when no invoice is available.
                                </CollectSectionSubtitle>
                            </Box>
                            {canMutate && (
                                <CollectPrimaryButton
                                    startIcon={<AddchartOutlined />}
                                    onClick={openDialog}
                                    disabled={!businessId}
                                >
                                    New prediction
                                </CollectPrimaryButton>
                            )}
                        </FlexBetween>
                    </CollectSectionHeader>

                    {error && (
                        <Typography sx={{ color: "#dc2626", mt: 1 }}>{error}</Typography>
                    )}

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : predictions.length === 0 ? (
                        <CollectEmptyState>
                            <AutoGraphOutlined
                                sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }}
                            />
                            <CollectEmptyTitle>No predictions yet</CollectEmptyTitle>
                            <CollectEmptyHint>
                                {canMutate
                                    ? "Generate your first invoice prediction to forecast receivables behavior."
                                    : "No predictions have been generated yet. Ask an owner or admin to create one."}
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer sx={{ mt: 1 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>
                                            Customer
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>Scope</CollectTableHeadCell>
                                        <CollectTableHeadCell>Risk</CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Reliability
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            On-time
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Generated
                                        </CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {predictions.map((p) => {
                                        const isActive = selected?._id === p._id;
                                        return (
                                            <TableRow
                                                key={p._id}
                                                hover
                                                selected={isActive}
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => handleSelect(p)}
                                            >
                                                <CollectTableBodyCell>
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {p.customerDisplayName ||
                                                            "Customer"}
                                                    </Typography>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {p.predictionType === "invoice" &&
                                                        p.invoiceDocNumber
                                                        ? `Invoice ${p.invoiceDocNumber}`
                                                        : "Customer fallback"}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            PREDICTION_RISK_LEVEL_LABELS[
                                                            p.customerRiskLevel
                                                            ]
                                                        }
                                                        sx={{
                                                            fontWeight: 700,
                                                            backgroundColor:
                                                                RISK_COLORS[
                                                                p.customerRiskLevel
                                                                ] + "1a",
                                                            color: RISK_COLORS[
                                                                p.customerRiskLevel
                                                            ],
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    {p.reliabilityScore}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    {Math.round(
                                                        (p.onTimeProbability ?? 0) * 100,
                                                    )}
                                                    %
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {fmtDate(p.generatedAt)}
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}
                    {total > predictions.length && (
                        <Typography
                            sx={{
                                fontFamily: FONT,
                                fontSize: "0.8rem",
                                color: "#6b7280",
                                mt: 1.5,
                            }}
                        >
                            Showing {predictions.length} of {total} predictions.
                        </Typography>
                    )}
                </CollectSectionCard>

                {/* Selected prediction detail */}
                {loadingDetail ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : selected ? (
                    <>
                        <PredictionSummary
                            prediction={selected}
                            canMutate={canMutate}
                            applying={applying}
                            onApply={handleApply}
                        />
                        <PredictionCharts charts={charts} />
                        <WhatIfSimulator
                            prediction={selected}
                            canMutate={canMutate}
                            onScenarioSaved={handleScenarioSaved}
                        />
                        <SavedScenarios prediction={selected} />

                        <CollectSectionCard>
                            <CollectSectionHeader>
                                <Box>
                                    <CollectSectionTitle>
                                        Audit history
                                    </CollectSectionTitle>
                                    <CollectSectionSubtitle>
                                        Prediction generation, simulations, and applies
                                        are recorded here for the selected invoice or
                                        customer.
                                    </CollectSectionSubtitle>
                                </Box>
                            </CollectSectionHeader>

                            {activitiesLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : activities.length === 0 ? (
                                <CollectEmptyState>
                                    <CollectEmptyTitle>
                                        No audit events yet
                                    </CollectEmptyTitle>
                                    <CollectEmptyHint>
                                        Generate a prediction, run a scenario, or apply a
                                        recommendation to populate the history.
                                    </CollectEmptyHint>
                                </CollectEmptyState>
                            ) : (
                                <CollectTableContainer sx={{ mt: 1 }}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <CollectTableHeadCell>
                                                    Event
                                                </CollectTableHeadCell>
                                                <CollectTableHeadCell>
                                                    Details
                                                </CollectTableHeadCell>
                                                <CollectTableHeadCell>
                                                    Actor
                                                </CollectTableHeadCell>
                                                <CollectTableHeadCell align="right">
                                                    When
                                                </CollectTableHeadCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {activities.map((item) => (
                                                <TableRow key={item._id} hover>
                                                    <CollectTableBodyCell>
                                                        <Chip
                                                            size="small"
                                                            label={
                                                                ACTIVITY_TYPE_LABELS[
                                                                item.type
                                                                ] || item.type
                                                            }
                                                            sx={{ fontWeight: 700 }}
                                                        />
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {item.title}
                                                        </Typography>
                                                        {item.description && (
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "0.82rem",
                                                                    color: "#64748b",
                                                                }}
                                                            >
                                                                {item.description}
                                                            </Typography>
                                                        )}
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell>
                                                        {item.actorDisplayName ||
                                                            item.actorType}
                                                    </CollectTableBodyCell>
                                                    <CollectTableBodyCell align="right">
                                                        {fmtDate(item.occurredAt)}
                                                    </CollectTableBodyCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CollectTableContainer>
                            )}
                        </CollectSectionCard>
                    </>
                ) : null}
            </CollectContentArea>

            {/* Generate prediction dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>New invoice prediction</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#64748b", mb: 2, fontSize: "0.875rem" }}>
                        Pick a customer and then select the open invoice you want to
                        forecast. If no invoice is available, the prediction falls back
                        to a customer-level receivables view.
                    </Typography>
                    <Autocomplete
                        options={customerOptions}
                        loading={customerLoading}
                        value={selectedCustomer}
                        onChange={(_, v) => {
                            setSelectedCustomer(v);
                            setSelectedInvoiceId("");
                        }}
                        getOptionLabel={(o) => o.displayName || "Customer"}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input") searchCustomers(value);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Customer"
                                placeholder="Search customers…"
                            />
                        )}
                    />

                    {selectedCustomer && (
                        <TextField
                            select
                            fullWidth
                            label={primaryInvoiceRequired ? "Invoice" : "Invoice (optional)"}
                            value={selectedInvoiceId}
                            onChange={(e) => setSelectedInvoiceId(e.target.value)}
                            sx={{ mt: 2 }}
                            helperText={
                                invoiceOptions.length === 0
                                    ? "No invoices found — a customer-level receivables prediction will be generated."
                                    : "Invoice-first forecasting is the default, but you can switch to customer-level (all invoices)."
                            }
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value={CUSTOMER_LEVEL_VALUE}>
                                Customer-level (all invoices)
                            </MenuItem>
                            {invoiceOptions.map((inv) => (
                                <MenuItem key={inv._id} value={inv._id}>
                                    {inv.docNumber
                                        ? `Invoice ${inv.docNumber}`
                                        : "Invoice"}{" "}
                                    · {inv.status}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </DialogContent>
                <DialogActions>
                    <CollectGhostButton onClick={() => setDialogOpen(false)}>
                        Cancel
                    </CollectGhostButton>
                    <CollectPrimaryButton
                        onClick={handleGenerate}
                        disabled={
                            generating ||
                            invoiceLoading ||
                            !selectedCustomer ||
                            !selectedInvoiceId
                        }
                        startIcon={
                            generating ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <AutoGraphOutlined />
                            )
                        }
                    >
                        {generating ? "Generating…" : "Generate"}
                    </CollectPrimaryButton>
                </DialogActions>
            </Dialog>
        </CollectPageWrapper>
    );
};

export default CollectPredictions;
